import { useState, useEffect } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useAuth } from '../context'
import { homeRouteForRole } from '../lib/roles'
import { setLanguage, LANGUAGES } from '../i18n'
import { safeStorage } from '../lib/safe-storage'

const LANG_META: Record<string, { hint: string; flag: string }> = {
  en: { hint: 'English',    flag: '🇬🇧' },
  hi: { hint: 'Hindi',     flag: '🇮🇳' },
  mr: { hint: 'Marathi',   flag: '🇮🇳' },
  bn: { hint: 'Bangla',    flag: '🇧🇩' },
  te: { hint: 'Telugu',    flag: '🇮🇳' },
  ta: { hint: 'Tamil',     flag: '🇮🇳' },
  kn: { hint: 'Kannada',   flag: '🇮🇳' },
  gu: { hint: 'Gujarati',  flag: '🇮🇳' },
}

const PROMPT_CYCLE = [
  'अपनी भाषा चुनें',
  'Choose Language',
  'ভাষা বেছে নিন',
  'మీ భాషను ఎంచుకోండి',
  'உங்கள் மொழியை தேர்ந்தெடுக்கவும்',
  'ನಿಮ್ಮ ಭಾಷೆ ಆಯ್ಕೆ ಮಾಡಿ',
  'તમારી ભાષા પસંદ કરો',
  'तुमची भाषा निवडा',
]

export default function LanguagePicker() {
  const navigate = useNavigate()
  const { user, loading, role } = useAuth()
  const [selected, setSelected] = useState<string | null>(null)
  const [promptIdx, setPromptIdx] = useState(0)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false)
      setTimeout(() => {
        setPromptIdx(i => (i + 1) % PROMPT_CYCLE.length)
        setVisible(true)
      }, 300)
    }, 2200)
    return () => clearInterval(interval)
  }, [])

  if (loading) {
    return <div data-testid="auth-loading" style={{ display: 'none' }} aria-hidden />
  }

  if (user) {
    return <Navigate to={homeRouteForRole(role)} replace />
  }

  const handleSelect = (code: string) => {
    setSelected(code)
    void (async () => {
      await setLanguage(code)
      safeStorage.set('lang_selected', 'true')
      setTimeout(() => navigate('/welcome'), 320)
    })()
  }

  return (
    <div style={styles.page}>
      <div style={styles.bgGlow} />

      <div style={styles.top}>
        <img src="/logo.png" alt="RailSahayak" style={styles.logo} />
        <div style={styles.appName}>RailSahayak</div>
        <div style={styles.tagline}>Railway Maintenance Evaluation</div>
      </div>

      <div style={styles.promptWrap}>
        <div
          style={{
            ...styles.prompt,
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(6px)',
          }}
        >
          {PROMPT_CYCLE[promptIdx]}
        </div>
      </div>

      <div style={styles.grid}>
        {LANGUAGES.map((lang) => {
          const meta = LANG_META[lang.code]
          const isSelected = selected === lang.code
          return (
            <button
              key={lang.code}
              style={{
                ...styles.tile,
                ...(isSelected ? styles.tileSelected : {}),
              }}
              onClick={() => handleSelect(lang.code)}
              disabled={selected !== null}
            >
              <span style={styles.tileFlag}>{meta.flag}</span>
              <span style={styles.tileName}>{lang.name}</span>
              <span style={styles.tileHint}>{meta.hint}</span>
              {isSelected && <span style={styles.tileCheck}>✓</span>}
            </button>
          )
        })}
      </div>

      <div style={styles.footer}>
        <div style={styles.footerDots}>
          {[0, 1, 2].map(i => (
            <span key={i} style={{ ...styles.dot, ...(i === 0 ? styles.dotActive : {}) }} />
          ))}
        </div>
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#0c1117',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 20px 24px',
    position: 'relative',
    overflow: 'hidden',
  },
  bgGlow: {
    position: 'absolute',
    top: -120,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 480,
    height: 480,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,107,53,0.12) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  top: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 24,
  },
  logo: {
    width: 72,
    height: 72,
    objectFit: 'contain',
    marginBottom: 12,
  },
  appName: {
    color: '#fff',
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: -0.5,
    marginBottom: 4,
  },
  tagline: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 12,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  promptWrap: {
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  prompt: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: 18,
    fontWeight: 600,
    textAlign: 'center',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
  },
  grid: {
    width: '100%',
    maxWidth: 400,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    flex: 1,
  },
  tile: {
    background: 'rgba(255,255,255,0.05)',
    border: '1.5px solid rgba(255,255,255,0.09)',
    borderRadius: 16,
    padding: '18px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    transition: 'transform 0.15s ease, background 0.15s ease, border-color 0.15s ease',
    position: 'relative',
    minHeight: 100,
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  tileSelected: {
    background: 'rgba(255,107,53,0.18)',
    borderColor: '#ff6b35',
    transform: 'scale(0.96)',
  },
  tileFlag: {
    fontSize: 22,
    lineHeight: 1,
    marginBottom: 2,
  },
  tileName: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.2,
    textAlign: 'center',
  },
  tileHint: {
    color: 'rgba(255,255,255,0.38)',
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  tileCheck: {
    position: 'absolute',
    top: 10,
    right: 12,
    color: '#ff6b35',
    fontSize: 16,
    fontWeight: 700,
  },
  footer: {
    marginTop: 28,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  footerDots: {
    display: 'flex',
    gap: 6,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.2)',
  },
  dotActive: {
    background: '#ff6b35',
    width: 20,
    borderRadius: 3,
  },
}
